import { api } from "../api/client";
import type { WindowDetail, WindowTick } from "../api/schemas";

interface OrderWindowLite {
  slug: string;
  result: string;
  historical_no_fill: boolean;
  direction: "UP" | "DOWN" | null;
  winning_direction: "UP" | "DOWN" | null;
  favorite_odds: string | null;
  pnl_usdc: number;
  trade_size_usdc: number | null;
  win_streak_at_bet: number;
  decision_outcome: string;
  check_trace: unknown[];
}

export interface ExportProgress {
  done: number;
  total: number;
  current: string | null;
}

interface ExportedWindow {
  slug: string;
  title: string;
  result: string;
  historical_no_fill: boolean;
  direction: "UP" | "DOWN" | null;
  winning_direction: "UP" | "DOWN" | null;
  favorite_odds: string | null;
  pnl_usdc: number;
  trade_size_usdc: number | null;
  win_streak_at_bet: number;
  decision_outcome: string;
  market: {
    window_start: string | null;
    window_end: string | null;
    btc_open: string | null;
    btc_close: string | null;
    btc_delta: string | null;
  };
  ticks: WindowTick[];
  check_trace: unknown[];
  detail_error?: string;
  ticks_error?: string;
  trace_error?: string;
}

function extractMessage(err: unknown): string {
  if (err && typeof err === "object" && "message" in err) {
    const m = (err as { message?: unknown }).message;
    if (typeof m === "string") return m;
  }
  return String(err);
}

function formatWindowTitle(start: Date): string {
  const end = new Date(start.getTime() + 5 * 60 * 1000);
  const fmt = (d: Date) => {
    const hours24 = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
    return `${hours12}:${minutes}`;
  };
  const suffix = end.getHours() >= 12 ? "PM" : "AM";
  return `${fmt(start)}-${fmt(end)}${suffix}`;
}

function titleFromSlug(slug: string): string {
  const parts = slug.split("-");
  const last = parts[parts.length - 1];
  const sec = Number(last);
  if (!Number.isFinite(sec)) return slug;
  const d = new Date(sec * 1000);
  if (!Number.isFinite(d.getTime())) return slug;
  return formatWindowTitle(d);
}

async function withConcurrency<T, R>(
  items: T[],
  limit: number,
  worker: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let next = 0;
  async function run() {
    while (true) {
      const i = next++;
      if (i >= items.length) return;
      results[i] = await worker(items[i]!, i);
    }
  }
  const runners = Array.from({ length: Math.min(limit, items.length) }, () => run());
  await Promise.all(runners);
  return results;
}

async function fetchWindowExport(
  runId: string,
  row: OrderWindowLite,
  preferEngineTrace: boolean,
): Promise<ExportedWindow> {
  const [detailRes, ticksRes, traceRes] = await Promise.all([
    api.windows.detail(row.slug).then(
      (d) => ({ ok: true as const, value: d }),
      (err) => ({ ok: false as const, err: extractMessage(err) }),
    ),
    api.windows.ticks(row.slug).then(
      (d) => ({ ok: true as const, value: d.items }),
      (err) => ({ ok: false as const, err: extractMessage(err) }),
    ),
    preferEngineTrace
      ? api.simulations.outcomeTrace(runId, row.slug).then(
          (d) => ({ ok: true as const, value: d.check_trace as unknown[] }),
          (err) => ({ ok: false as const, err: extractMessage(err) }),
        )
      : Promise.resolve({ ok: true as const, value: row.check_trace }),
  ]);

  const detail: WindowDetail | null = detailRes.ok ? detailRes.value : null;
  const detailErr = detailRes.ok ? undefined : detailRes.err;
  const ticks: WindowTick[] = ticksRes.ok ? ticksRes.value : [];
  const ticksErr = ticksRes.ok ? undefined : ticksRes.err;
  const trace: unknown[] = traceRes.ok ? traceRes.value : row.check_trace;
  const traceErr = traceRes.ok ? undefined : traceRes.err;

  let title = titleFromSlug(row.slug);
  if (detail) {
    const ms = Date.parse(detail.window_start);
    if (Number.isFinite(ms)) title = formatWindowTitle(new Date(ms));
  }

  return {
    slug: row.slug,
    title,
    result: row.result,
    historical_no_fill: row.historical_no_fill,
    direction: row.direction,
    winning_direction: row.winning_direction,
    favorite_odds: row.favorite_odds,
    pnl_usdc: row.pnl_usdc,
    trade_size_usdc: row.trade_size_usdc,
    win_streak_at_bet: row.win_streak_at_bet,
    decision_outcome: row.decision_outcome,
    market: {
      window_start: detail?.window_start ?? null,
      window_end: detail?.window_end ?? null,
      btc_open: detail?.btc_open ?? null,
      btc_close: detail?.btc_close ?? null,
      btc_delta: detail?.btc_delta ?? null,
    },
    ticks,
    check_trace: trace,
    ...(detailErr ? { detail_error: detailErr } : {}),
    ...(ticksErr ? { ticks_error: ticksErr } : {}),
    ...(traceErr ? { trace_error: traceErr } : {}),
  };
}

export interface ExportArgs {
  runId: string;
  orderWindows: OrderWindowLite[];
  summary: unknown;
  configOverride: Record<string, unknown> | null;
  subtractNoFillsFromResults: boolean;
  excludeStaleOdds: boolean;
  fromInput: string;
  toInput: string;
  onProgress?: (p: ExportProgress) => void;
  preferEngineTrace?: boolean;
  concurrency?: number;
}

export async function exportSimulatedData(args: ExportArgs): Promise<void> {
  const total = args.orderWindows.length;
  let done = 0;
  args.onProgress?.({ done, total, current: null });
  const windows = await withConcurrency(args.orderWindows, args.concurrency ?? 6, async (row) => {
    args.onProgress?.({ done, total, current: row.slug });
    const out = await fetchWindowExport(args.runId, row, args.preferEngineTrace ?? true);
    done += 1;
    args.onProgress?.({ done, total, current: row.slug });
    return out;
  });

  const payload = {
    run_id: args.runId,
    exported_at: new Date().toISOString(),
    from: args.fromInput || null,
    to: args.toInput || null,
    subtract_no_fills_from_results: args.subtractNoFillsFromResults,
    exclude_stale_odds: args.excludeStaleOdds,
    config_override: args.configOverride,
    summary: args.summary,
    windows,
  };

  const json = JSON.stringify(payload, null, 2);
  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    const stamp = new Date().toISOString().replace(/[:.]/g, "-");
    a.href = url;
    a.download = `manual-sim-${args.runId}-${stamp}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}
